import { createFeatureSelector, createSelector } from '@ngrx/store';
import { selectRouteParam } from '../router/router.selector';

import * as fromUser from './user.reducer';
import * as _ from 'lodash';
import { getPartner } from '../partner/partner.selectors';

import * as CurrentUserSelectors from './../../../app/@store/current-user/current-user.selectors';

const { selectEntities } = fromUser.adapter.getSelectors();

export const selectUserState = createFeatureSelector<fromUser.State>(fromUser.userFeatureKey);
const listSelectors = fromUser.listAdapter.getSelectors((state: any) => state.list);

/************************
 * LEGACY
 *
 * ALL Users Selectores
 *************************/

export const getAllUsers = createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['allUser', 'data'], []));
export const isAllUsersLoading = createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['allUser', 'loading'], false));

export const getAllUsersCount = createSelector(selectUserState, getAllUsers, (state: fromUser.State, users) => _.size(users));

/************************
 * NEW Pagination Style
 *************************/
export const getUsersTotalInStore = createSelector(selectUserState, listSelectors.selectTotal);

/**
 * Get list config (sorting etc.)
 */
export const getUserListConfig = createSelector(selectUserState, (state: fromUser.State) => state?.list?.listConfig);

/**
 * Get User list
 */
export const getUsers = createSelector(selectUserState, listSelectors.selectAll);

/**
 * Get User list meta data (total etc.)
 */
export const getUsersMeta = createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['list', 'meta'], { total: 0, count: 0, offset: 0, limit: 0, page: 0 }));
export const getUsersTotal = createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['list', 'meta', 'total']));

export const getUsersTotalUnfiltered = createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['total'], 0));

/**
 * Get User list links, to load next bunch of data
 */
export const getUsersLinks = createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['list', 'links']));

/**
 * Get if User list is loading
 */
export const isUserListLoading = createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['list', 'loading'], false));

/**
 * Get if User list is loading next bunch of data
 */
export const isUserListLoadingNext = createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['list', 'loadingNext'], false));

/**
 * get if User list is loaded
 */
export const isUserListLoaded = createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['list', 'loaded'], false));

/**
 * get User
 */
export const getUser = (props: { uuid: string }) => createSelector(selectUserState, (state: fromUser.State) => _.get(selectEntities(state), [props.uuid]));

/**
 * get User last error
 */
export const getUserError = (props: { uuid: string }) => createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['error', props.uuid]));

/**
 * get if User is loading
 */
export const isUserLoading = (props: { uuid: string }) => createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['loading', props.uuid], false));

/**
 * get if User is patching
 */
export const isUserPatching = (props: { uuid: string }) => createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['patching', props.uuid], false));

/**
 * get if User is deleting
 */
export const isUserDeleting = (props: { uuid: string }) => createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['deleting', props.uuid], false));

/**
 * get if User is ready loaded
 */
export const isUserLoaded = (props: { uuid: string }) => createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['loaded', props.uuid], false));

/**
 * get selected User (by route param)
 */
export const getSelectedUser = createSelector(selectUserState, selectRouteParam('user_uuid'), (state: fromUser.State, user_uuid) =>
  _.get(selectEntities(state), [user_uuid])
);

/**
 * get selected User (by route param) last error
 */
export const getSelectedUserError = createSelector(selectUserState, selectRouteParam('user_uuid'), (state: fromUser.State, user_uuid) =>
  _.get(state, ['error', user_uuid])
);

/**
 * get if selected User (by route param) is loading
 */
export const isSelectedUserLoading = createSelector(selectUserState, selectRouteParam('user_uuid'), (state: fromUser.State, user_uuid) =>
  _.get(state, ['loading', user_uuid], false)
);

/**
 * get if selected User (by route param) is patching
 */
export const isSelectedUserPatching = createSelector(selectUserState, selectRouteParam('user_uuid'), (state: fromUser.State, user_uuid) =>
  _.get(state, ['patching', user_uuid], false)
);

/**
 * get if selected User (by route param) is deleting
 */
export const isSelectedUserDeleting = createSelector(selectUserState, selectRouteParam('user_uuid'), (state: fromUser.State, user_uuid) =>
  _.get(state, ['deleting', user_uuid], false)
);

/**
 * get if selected User (by route param) is ready loaded
 */
export const isSelectedUserLoaded = createSelector(selectUserState, selectRouteParam('user_uuid'), (state: fromUser.State, user_uuid) =>
  _.get(state, ['loaded', user_uuid], false)
);

/**
 * Check if the currentSelectedUser is the current User.
 */
export const isSelectedUserCurrentUser = createSelector(selectUserState, selectRouteParam('user_uuid'), CurrentUserSelectors.uuid, (state: fromUser.State, user_uuid, uuid) => {
  return user_uuid === uuid;
});

/**
 * get current search term
 */
export const getSearch = createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['list', 'search'], ''));

export const getSelectedUserAccounts = createSelector(getSelectedUser, user => user?.relations?.contracts || []);

// Autocomplete
export const getAutocompleteUsers = createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['autocomplete'], []));
export const isAutocompleteLoading = createSelector(selectUserState, (state: fromUser.State) => _.get(state, ['autocomplete_loading'], false));

export const getFullSession = createSelector(selectUserState, getPartner, CurrentUserSelectors.user, (state: fromUser.State, partner, user) => {
  return {
    user: user,
    partner: partner
  };
});
